import { CSAT_RATING_VALUES } from '@bv/types/constants/csat';

import { useRestoreFieldTypes } from '../../composables/restore-field-types';
import countries from '../constants/countries';
import { contactFieldsOptions, contactFieldOptionsCache } from '../contact-fields-options';

import { channelsOptions, contactTagsOptions } from './trigger';

import type {
  FieldOption,
  SelectField,
  StepConfig,
  StepFormField,
  FieldValue,
  FieldValues,
} from '../../types/workflow';

interface BranchConditionParentValues {
  attributeKey?:
    | 'contactField'
    | 'variable'
    | 'lastIncomingMessage'
    | 'lastOutgoingMessage'
    | 'lastOutgoingMessageSource'
    | 'csatRatingValue'
    | 'channel'
    | 'contactTags';
  key?: string;
  operator?: string;
  value?: string;
}

type OperatorOption = { label: string; value: string };

/** Condition types that use attributeKey only (no separate field key dropdown). */
const ATTRIBUTE_KEYS_WITHOUT_FIELD_KEY = new Set([
  'lastIncomingMessage',
  'lastOutgoingMessage',
  'lastOutgoingMessageSource',
  'csatRatingValue',
  'channel',
  'contactTags',
]);

const MULTI_VALUE_OPERATORS = new Set(['hasAnyOf', 'hasNoneOf', 'hasAllOf']);

function isMultiValueOperator(operator?: string): boolean {
  return typeof operator === 'string' && MULTI_VALUE_OPERATORS.has(operator);
}

const OPS_EQUAL_NOT: OperatorOption[] = [
  { label: 'Equal to', value: 'equalTo' },
  { label: 'Not equal to', value: 'notEqualTo' },
];

const OPS_EXISTS: OperatorOption[] = [
  { label: 'Exists', value: 'exists' },
  { label: 'Not exists', value: 'notExists' },
];

const OPS_CONTACT_TAG: OperatorOption[] = [
  ...OPS_EQUAL_NOT,
  ...OPS_EXISTS,
  { label: 'Has any of', value: 'hasAnyOf' },
  { label: 'Has all of', value: 'hasAllOf' },
  { label: 'Has none of', value: 'hasNoneOf' },
];

const TEXT_FIELD_KEYS = [
  'full_name',
  'email',
  'company_name',
  'workflowVariable',
  'conversationFirstIncomingMessage',
] as const;

const lastOutgoingMessageSourceOptions: FieldOption[] = [
  { label: 'User', value: 'user' },
  { label: 'Workflow', value: 'workflow' },
  { label: 'Broadcast', value: 'broadcast' },
];

const lastOutgoingMessageSourceOperators: OperatorOption[] = [
  { label: 'Equal to', value: 'equalTo' },
  { label: 'Not equal to', value: 'notEqualTo' },
  { label: 'Has Any Of', value: 'hasAnyOf' },
  { label: 'Has None Of', value: 'hasNoneOf' },
];

/**
 * Values-field options for branch conditions. Must stay a function `(parentValues) => …`
 * so FormField can resolve paginated getters (channels/tags). Do not assign
 * `channelsOptions` / `contactTagsOptions` directly to `field.options` — that invokes
 * the API with parentValues as config and breaks the select.
 */
function branchConditionValuesOptions(parentValues: BranchConditionParentValues) {
  const { attributeKey, key, operator } = parentValues;

  if (attributeKey === 'csatRatingValue') {
    return CSAT_RATING_VALUES as FieldOption[];
  }

  if (attributeKey === 'channel') {
    return channelsOptions;
  }

  if (attributeKey === 'contactTags') {
    return contactTagsOptions;
  }

  if (attributeKey === 'lastOutgoingMessageSource') {
    return lastOutgoingMessageSourceOptions;
  }

  if (key && typeof key === 'string' && !isMultiValueOperator(operator)) {
    const selectedOption = contactFieldOptionsCache?.[key];
    if (selectedOption && selectedOption.type === 'list') {
      return (selectedOption.list_values || []).map((val) => ({
        label: val,
        value: val,
      }));
    }
  }

  if (key === 'country_name' && !isMultiValueOperator(operator)) {
    return countries;
  }

  return [];
}

const { updateValidationByKey, triggerFieldOnChange, isMultiSelectField, clearContactValidation } =
  useRestoreFieldTypes();
const headerTextColor = '#663C00';
const headerColor = '#FFDCAA';

const branch: StepConfig = {
  // Design
  icon: 'branch',
  headerTextColor,
  headerColor,
  title: 'Branch',
  text: 'Branch #1',
  category: 'Router',
  desc: 'Allows workflow branches to be created based on contact fields, tags, variables or previous interactions.',
  primaryKey: 'conditions.0.attributeKey',
  connectors: {
    IfCondition: {
      headerTextColor: headerTextColor,
      headerColor: headerColor,
      category: 'Connector',
      title: 'Branch 01',
    },
    ElsePath: {
      headerTextColor: headerTextColor,
      headerColor: headerColor,
      category: 'Connector',
      title: 'else',
    },
  },

  // Form
  formFields: [
    {
      key: 'branches',
      type: 'branchRepeater',
      defaultValue: [],
      config: {
        maxBranches: 10,
        minBranches: 1,
        conditionStructure: [
          {
            key: 'attributeKey',
            type: 'select',
            defaultValue: 'contactField',
            options: [
              { label: 'Contact Field', value: 'contactField' },
              { label: 'Contact tag', value: 'contactTags' },
              { label: 'Variable', value: 'variable' },
              { label: 'Last Incoming Message', value: 'lastIncomingMessage' },
              { label: 'Last Outgoing Message', value: 'lastOutgoingMessage' },
              { label: 'Last Outgoing Message Source', value: 'lastOutgoingMessageSource' },
              { label: 'Csat Rating Value', value: 'csatRatingValue' },
              { label: 'Channel', value: 'channel' },
            ],
            onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
              allValues.operator = 'equalTo';

              const attr = typeof value === 'string' ? value : '';
              allFields.key.show = !ATTRIBUTE_KEYS_WITHOUT_FIELD_KEY.has(attr);

              if (value === 'contactTags') {
                allValues.key = 'contactTags';
              } else if (allValues.key === 'contactTags') {
                allValues.key = '';
              }

              if (value === 'csatRatingValue') {
                allFields.values.type = 'select';
                (allFields.values as SelectField).options = CSAT_RATING_VALUES as FieldOption[];
              } else if (value === 'lastOutgoingMessageSource') {
                allFields.values.type = 'select';
                (allFields.values as SelectField).options = lastOutgoingMessageSourceOptions;
              } else if (value === 'channel' || value === 'contactTags') {
                allFields.values.type = 'select';
                (allFields.values as SelectField).options = branchConditionValuesOptions;
              } else {
                allFields.values.type = 'text';
                (allFields.values as SelectField).options = branchConditionValuesOptions;
              }

              if (allFields['values'] && allFields['values'].validation) {
                clearContactValidation(allFields['values']);
                allFields['values'].validation.textNoSpecialChars = false;
              }
            },
          },
          {
            key: 'key',
            type: 'select',
            searchKey: 'filter[q]',
            defaultValue: '',
            dependsOn: ['attributeKey'],
            validation: {
              required: true,
            },
            show: (parentValues: BranchConditionParentValues) => {
              const attr = parentValues.attributeKey;
              return !attr || !ATTRIBUTE_KEYS_WITHOUT_FIELD_KEY.has(attr);
            },
            options: (parentValues: BranchConditionParentValues) => {
              if (parentValues.attributeKey === 'contactField') {
                return contactFieldsOptions();
              } else if (parentValues.attributeKey === 'variable') {
                return 'variables';
              }
              return [];
            },
            onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
              allValues.operator = 'equalTo';

              if (typeof value === 'string') {
                updateValidationByKey(allFields.values, value);

                const operator = (allValues.operator as string) || '';
                if (operator === 'contains' || operator === 'notContains') {
                  if (allFields.values && allFields.values.validation) {
                    allFields.values.validation.email = false;
                    allFields.values.validation.phone = false;
                  }
                }

                const selectedOption = contactFieldOptionsCache?.[value];
                if (selectedOption && selectedOption.type === 'list') {
                  if (!isMultiValueOperator(operator)) {
                    (allFields.values as SelectField).type = 'select';
                    (allFields.values as SelectField).options = (selectedOption.list_values || []).map((val) => ({
                      label: val,
                      value: val,
                    }));
                  }
                }

                if (value === 'country_name') {
                  if (!isMultiValueOperator(operator)) {
                    (allFields.values as SelectField).type = 'select';
                    (allFields.values as SelectField).options = countries;
                  }
                }

                triggerFieldOnChange(allFields.operator, operator, allValues, allFields);
              }
            },
          },
          {
            key: 'operator',
            type: 'select',
            defaultValue: 'equalTo',
            dependsOn: ['attributeKey', 'key'],
            options: (parentValues: BranchConditionParentValues) => {
              const { attributeKey, key } = parentValues;

              const commonOperators: OperatorOption[] = [...OPS_EQUAL_NOT, ...OPS_EXISTS];

              const textOperators: OperatorOption[] = [
                { label: 'Contains', value: 'contains' },
                { label: 'Does not contain', value: 'notContains' },
              ];

              const multiSelectOperators: OperatorOption[] = [
                { label: 'Has Any Of', value: 'hasAnyOf' },
                { label: 'Has None Of', value: 'hasNoneOf' },
              ];

              const variableOperators: OperatorOption[] = [
                { label: 'Has any of', value: 'hasAnyOf' },
                { label: 'Has all of', value: 'hasAllOf' },
              ];

              const lastMessageOperators: OperatorOption[] = [...OPS_EQUAL_NOT, ...textOperators, ...OPS_EXISTS];

              if (attributeKey === 'lastIncomingMessage' || attributeKey === 'lastOutgoingMessage') {
                return lastMessageOperators;
              }

              if (attributeKey === 'lastOutgoingMessageSource') {
                return lastOutgoingMessageSourceOperators;
              }

              let operators = [...commonOperators];

              if (key && TEXT_FIELD_KEYS.includes(key as (typeof TEXT_FIELD_KEYS)[number])) {
                operators = [...operators, ...textOperators];
              }

              if (key && isMultiSelectField(key)) {
                operators = [...operators, ...multiSelectOperators];
              }

              if (attributeKey === 'variable') {
                operators = [...operators, ...textOperators, ...variableOperators];
              }

              if (attributeKey === 'csatRatingValue' || attributeKey === 'channel') {
                return [...OPS_EQUAL_NOT];
              }

              if (attributeKey === 'contactTags') {
                return [...OPS_CONTACT_TAG];
              }

              return operators;
            },
            onChange: (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => {
              const key = (allValues.key as string) || '';
              const attributeKey = allValues.attributeKey as string | undefined;

              if (attributeKey === 'lastOutgoingMessageSource') {
                if (typeof value === 'string' && (value === 'hasAnyOf' || value === 'hasNoneOf')) {
                  allFields['values'].type = 'multiSelect';
                } else {
                  allFields['values'].type = 'select';
                }
                (allFields['values'] as SelectField).options = lastOutgoingMessageSourceOptions;
              } else if (typeof value === 'string' && isMultiValueOperator(value)) {
                allFields['values'].type = allValues.attributeKey === 'contactTags' ? 'multiSelect' : 'list';
                if (allValues.attributeKey === 'contactTags') {
                  (allFields['values'] as SelectField).options = branchConditionValuesOptions;
                }
              } else {
                const selectedOption = contactFieldOptionsCache?.[key];
                if (selectedOption && selectedOption.type === 'list') {
                  (allFields['values'] as SelectField).type = 'select';
                  (allFields['values'] as SelectField).options = (selectedOption.list_values || []).map((val) => ({
                    label: val,
                    value: val,
                  }));
                } else if (key === 'country_name') {
                  (allFields['values'] as SelectField).type = 'select';
                  (allFields['values'] as SelectField).options = countries;
                } else if (allValues.attributeKey === 'channel') {
                  (allFields['values'] as SelectField).type = 'select';
                  (allFields['values'] as SelectField).options = branchConditionValuesOptions;
                } else if (allValues.attributeKey === 'contactTags') {
                  (allFields['values'] as SelectField).type = 'select';
                  (allFields['values'] as SelectField).options = branchConditionValuesOptions;
                } else if (
                  allValues.attributeKey !== 'csatRatingValue' &&
                  allValues.attributeKey !== 'channel' &&
                  allValues.attributeKey !== 'contactTags' &&
                  allValues.attributeKey !== 'lastOutgoingMessageSource'
                ) {
                  allFields['values'].type = 'text';
                }
              }

              if (typeof value === 'string' && (value === 'contains' || value === 'notContains')) {
                if (allFields['values'] && allFields['values'].validation) {
                  allFields['values'].validation.email = false;
                  allFields['values'].validation.phone = false;
                }
              } else if (typeof value === 'string') {
                if (key && allFields['values']) {
                  updateValidationByKey(allFields['values'], key);
                }
              }
            },
          },
          {
            key: 'values',
            searchKey: (parentValues: BranchConditionParentValues) => {
              if (parentValues.attributeKey === 'contactTags') {
                return 'filter[q]';
              }
              return 'name';
            },
            type: (parentValues: BranchConditionParentValues) => {
              const { attributeKey, operator, key } = parentValues;

              if (attributeKey === 'csatRatingValue') {
                return 'select';
              }

              if (attributeKey === 'lastOutgoingMessageSource') {
                return operator === 'hasAnyOf' || operator === 'hasNoneOf' ? 'multiSelect' : 'select';
              }

              if (attributeKey === 'channel') {
                return 'select';
              }

              if (attributeKey === 'contactTags') {
                return isMultiValueOperator(operator) ? 'multiSelect' : 'select';
              }

              if (isMultiValueOperator(operator)) {
                return 'list';
              }

              if (key && typeof key === 'string') {
                const selectedOption = contactFieldOptionsCache?.[key];
                if (selectedOption && selectedOption.type === 'list') {
                  return 'select';
                }
              }

              if (key === 'country_name') {
                return 'select';
              }

              return 'text';
            },
            defaultValue: [],
            show: (parentValues: BranchConditionParentValues) => {
              return parentValues.operator !== 'exists' && parentValues.operator !== 'notExists';
            },
            dependsOn: ['operator', 'key', 'attributeKey'],
            placeholder: (parentValues: BranchConditionParentValues) => {
              const { attributeKey, operator } = parentValues;

              if (attributeKey === 'csatRatingValue') {
                return 'Select CSAT rating';
              }

              if (attributeKey === 'channel') {
                return 'Select channel';
              }

              if (attributeKey === 'lastOutgoingMessageSource') {
                return operator === 'hasAnyOf' || operator === 'hasNoneOf'
                  ? 'Select one or more sources'
                  : 'Select source';
              }

              if (attributeKey === 'contactTags') {
                return isMultiValueOperator(operator) ? 'Select tags' : 'Select tag';
              }

              if (isMultiValueOperator(operator)) {
                return 'Add values to the list';
              }

              return 'Enter value';
            },
            options: branchConditionValuesOptions,
            maxItems: (parentValues: BranchConditionParentValues) => {
              return isMultiValueOperator(parentValues.operator) ? 20 : 0;
            },
            validation: {
              required: true,
              email: false,
              phone: false,
            },
          },
        ],
        connectorConfig: {
          key: 'IfCondition',
          headerTextColor: headerTextColor,
          headerColor: headerColor,
          category: 'Connector',
        },
      },
      desc: 'Manage multiple branches with their conditions',
    },
  ],
};

export default branch;
